/**
 * Best-effort records: the fastest contiguous run of active lengths for each distance in the
 * ladder. A record lives **within a single stint** — because rests are their own stints, a window
 * can never span a rest (the mistake the reference app makes by filtering to active lengths first).
 * A drill length breaks a window rather than being skipped over. Pure — no DOM, no state.
 */
import { SWIM_STROKE } from '../domain/fields'
import type { Activity, Length, Stint } from '../domain/model'
import { paceSecondsPer100, toDisplayDistance } from './format'
import { displayUnit } from './stats'

/** Distances in display units (metres or yards), matching the pool's own unit. */
const LADDER = [50, 100, 200, 400, 500, 800, 1000, 1500] as const

export interface SwimRecord {
  /** Distance in display units, e.g. 100. */
  readonly distance: number
  /** Number of lengths in the window. */
  readonly lengths: number
  readonly timeMs: number
  readonly paceSecondsPer100: number
  readonly stintIndex: number
  readonly startOrdinal: number
  readonly endOrdinal: number
}

const EPSILON = 1e-6

/** Maximal runs of active, non-drill lengths within a stint (drills split the runs). */
function runs(stint: Stint): Length[][] {
  const out: Length[][] = []
  let current: Length[] = []
  for (const l of stint.lengths) {
    if (l.active && l.swimStroke !== SWIM_STROKE.drill) {
      current.push(l)
    } else if (current.length > 0) {
      out.push(current)
      current = []
    }
  }
  if (current.length > 0) out.push(current)
  return out
}

export function computeRecords(activity: Activity): SwimRecord[] {
  const unit = displayUnit(activity)
  const poolDisplay = toDisplayDistance(activity.poolLengthCm / 100, unit)
  if (poolDisplay <= 0) return []

  const best = new Map<number, SwimRecord>()

  activity.stints.forEach((stint, stintIndex) => {
    for (const run of runs(stint)) {
      // prefix[i] = Σ durations of run[0..i-1]
      const prefix = new Array<number>(run.length + 1)
      prefix[0] = 0
      for (let i = 0; i < run.length; i++) prefix[i + 1] = prefix[i]! + run[i]!.durationMs

      for (const distance of LADDER) {
        const k = distance / poolDisplay
        if (Math.abs(k - Math.round(k)) > EPSILON) continue // not a whole number of lengths
        const kk = Math.round(k)
        if (kk < 1 || kk > run.length) continue

        for (let start = 0; start + kk <= run.length; start++) {
          const timeMs = prefix[start + kk]! - prefix[start]!
          const existing = best.get(distance)
          if (!existing || timeMs < existing.timeMs) {
            best.set(distance, {
              distance,
              lengths: kk,
              timeMs,
              paceSecondsPer100: paceSecondsPer100(
                kk * (activity.poolLengthCm / 100),
                timeMs,
                unit,
              ),
              stintIndex,
              startOrdinal: run[start]!.ordinal,
              endOrdinal: run[start + kk - 1]!.ordinal,
            })
          }
        }
      }
    }
  })

  return LADDER.map((d) => best.get(d)).filter((r): r is SwimRecord => r !== undefined)
}
